import React, { useState, useEffect, useRef } from 'react';
import { Search, Download } from 'lucide-react';
import { logsData, LogEntry } from '../data/logsData';
import TrafficChart from './TrafficChart';
import './DatabasePage.css';

const DatabasePage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'time' | 'level' | 'component'>('time');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [logs, setLogs] = useState<LogEntry[]>(logsData);
  const [newLogId, setNewLogId] = useState<string>('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Function to generate a random log entry with current timestamp
  const generateNewLogEntry = (): LogEntry => {
    const levels: ('WARN' | 'INFO' | 'ERROR')[] = ['WARN', 'INFO', 'ERROR'];
    const components = [
      'c.c.a.u.DofUtils$',
      'o.a.k.c.c.i.ConsumerUtils',
      'o.a.k.c.c.i.ConsumerCoordinator',
      'c.c.t.Refresher',
      'c.c.s.ServiceMonitor',
      'c.c.d.DatabaseManager',
      'c.c.n.NetworkService',
      'c.c.a.AuthenticationService',
      'c.c.c.CacheManager',
      'c.c.m.MessageProcessor'
    ];

    const messageTemplates = [
      "Connection timeout to downstream service after 30s",
      "Database connection pool exhausted, waiting for available connection",
      "Cache miss for key: user_session_{id}, fetching from database",
      "Rate limit exceeded for client IP: {ip}, request rejected",
      "Message processing completed successfully in {time}ms",
      "Partition rebalance triggered for consumer group: {group}",
      "SSL certificate validation failed for endpoint: {endpoint}",
      "Memory usage warning: {percent}% of heap space used",
      "Failed to serialize object of type: {type}",
      "Async task completed with status: {status}",
      "Network latency spike detected: {latency}ms average",
      "Authentication failed for user: {user}",
      "Backup operation completed successfully",
      "Dead letter queue processing started",
      "Circuit breaker opened for service: {service}",
      "Missed to enrich exclusion details for: GzipInvalidPayload(java.lang.IllegalArgumentException: Illegal base64 character 20)"
    ];

    // Helper function to generate random hex string
    const generateRandomHex = (length: number): string => {
      const chars = '0123456789abcdef';
      let result = '';
      for (let i = 0; i < length; i++) {
        result += chars[Math.floor(Math.random() * chars.length)];
      }
      return result;
    };

    // Generate a current timestamp
    const now = new Date();
    const timeString = now.toISOString().slice(0, 19).replace('T', ' ');
    const tsNs = String(now.getTime()) + generateRandomHex(9);

    // Generate a random message
    const messageTemplate = messageTemplates[Math.floor(Math.random() * messageTemplates.length)];
    const message = messageTemplate
      .replace('{id}', generateRandomHex(8))
      .replace('{ip}', `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`)
      .replace('{time}', (Math.random() * 5000 + 100).toFixed(0))
      .replace('{group}', `consumer-group-${Math.floor(Math.random() * 10)}`)
      .replace('{endpoint}', `https://api-${Math.floor(Math.random() * 5)}.example.com`)
      .replace('{percent}', (Math.random() * 40 + 60).toFixed(1))
      .replace('{type}', `com.example.Type${Math.floor(Math.random() * 20)}`)
      .replace('{status}', ['SUCCESS', 'FAILED', 'PARTIAL'][Math.floor(Math.random() * 3)])
      .replace('{latency}', (Math.random() * 2000 + 50).toFixed(0))
      .replace('{user}', `user_${generateRandomHex(6)}`)
      .replace('{service}', `service-${Math.floor(Math.random() * 8)}`);

    return {
      id: `${tsNs.slice(0, 19)}_${generateRandomHex(8)}`,
      time: timeString,
      level: levels[Math.floor(Math.random() * levels.length)],
      component: components[Math.floor(Math.random() * components.length)],
      message: message,
      tsNs: tsNs
    };
  };

  // Setup and cleanup for real-time data simulation
  useEffect(() => {
    // Add a new log entry every 2-5 seconds
    const addNewLogEntry = () => {
      const newLogEntry = generateNewLogEntry();
      setLogs(prevLogs => [newLogEntry, ...prevLogs.slice(0, 749)]); // Keep only 750 logs max
      setNewLogId(newLogEntry.id); // Set the ID of the new log to animate it

      // Clear the new log ID after 2 seconds to stop the animation
      setTimeout(() => {
        setNewLogId('');
      }, 2000);
    };

    // Start the interval
    intervalRef.current = setInterval(addNewLogEntry, Math.random() * 3000 + 2000);

    // Clean up on component unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const filteredLogs = logs
    .filter(log => {
      const matchesSearch = log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.component.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLevel = levelFilter === 'all' || log.level === levelFilter;
      return matchesSearch && matchesLevel;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'time') {
        comparison = new Date(a.time).getTime() - new Date(b.time).getTime();
      } else if (sortBy === 'level') {
        comparison = a.level.localeCompare(b.level);
      } else if (sortBy === 'component') {
        comparison = a.component.localeCompare(b.component);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const getLevelBadgeClass = (level: string) => {
    switch (level) {
      case 'ERROR': return 'level-error';
      case 'WARN': return 'level-warn';
      case 'INFO': return 'level-info';
      default: return 'level-default';
    }
  };

  const formatTime = (timeString: string) => {
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (timeString: string) => {
    const date = new Date(timeString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit'
    });
  };

  const truncateMessage = (message: string, maxLength: number = 120) => {
    return message.length > maxLength ? message.substring(0, maxLength) + '...' : message;
  };

  return (
    <div className="database-page">
      <TrafficChart />
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
            window._uxa = window._uxa || [];
            window._uxa.push(["trackDynamicVariable", { key: "database", value: "database_value" }]);
          `
        }}
      />
      <div className="database-header">
        <div className="page-title">
          <h1>Database Logs</h1>
          <span className="logs-count">
            {filteredLogs.length} entries
            <span className="live-indicator" title="Live data streaming">• LIVE</span>
          </span>
        </div>

        <div className="logs-controls">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="level-filter"
          >
            <option value="all">All Levels</option>
            <option value="ERROR">Error</option>
            <option value="WARN">Warning</option>
            <option value="INFO">Info</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'time' | 'level' | 'component')}
            className="sort-select"
          >
            <option value="time">Sort by Time</option>
            <option value="level">Sort by Level</option>
            <option value="component">Sort by Component</option>
          </select>

          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-order-btn"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>

          <button className="export-btn">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="logs-table-container">
        <table className="logs-table">
          <thead>
            <tr>
              <th className="col-time">Time</th>
              <th className="col-level">Level</th>
              <th className="col-component">Component</th>
              <th className="col-message">Message</th>
              <th className="col-id">ID</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log) => (
              <tr key={log.id} className={`log-row ${log.id === newLogId ? 'new-entry' : ''}`}>
                <td className="col-time">
                  <div className="time-cell">
                    <div className="date">{formatDate(log.time)}</div>
                    <div className="time">{formatTime(log.time)}</div>
                  </div>
                </td>
                <td className="col-level">
                  <span className={`level-badge ${getLevelBadgeClass(log.level)}`}>
                    {log.level}
                  </span>
                </td>
                <td className="col-component">
                  <code className="component-name">{log.component}</code>
                </td>
                <td className="col-message">
                  <div className="message-cell" title={log.message}>
                    {truncateMessage(log.message)}
                  </div>
                </td>
                <td className="col-id">
                  <code className="log-id">{log.id.split('_')[1]}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DatabasePage;
